import { LeverageBadge, OrderSideBadge } from '@flipster/features/src/misc/badge';
import { FormattedMessage } from '@flipster/i18n';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import { QuoteCurrencyList, getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import { OrderMarginType, V2OrderSideType } from '@flipster/utils/types/base';

interface Props {
  side: V2OrderSideType;
  quantity: string;
  currency: string;
  pricePrecision: number;
  quoteCurrency: (typeof QuoteCurrencyList)[number];
  unrealizedPnl: string;
  calcUnrealizedPnlPercent?: string;
  entryPrice: string;
  margin: string;
  leverage: number;
  marginType: OrderMarginType;
}

const FlipSideCard = ({
  side,
  quantity,
  currency,
  pricePrecision,
  quoteCurrency,
  unrealizedPnl,
  calcUnrealizedPnlPercent,
  entryPrice,
  margin,
  leverage,
  marginType,
}: Props) => {
  return (
    <section
      className={css({
        position: 'relative',
        p: 16,
        overflow: 'hidden',
        rounded: 8,
        background: 'background.default.level-1',
        _before: {
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: 4,
          content: "''",
          background: side === 'Long' ? 'background.accent.buy' : 'background.accent.sell',
        },
      })}
    >
      <div className={flex({ gap: 4, color: 'inverse.white' })}>
        <Formatted.Number
          value={quantity}
          suffix={currency}
          toFixed
          abs
          css={{ textStyle: 'Text/L/Regular', w: '100%' }}
        />
        <OrderSideBadge orderSide={side} />
        <LeverageBadge leverage={leverage} marginType={marginType} />
      </div>
      <div className={flex({ gap: 4, pt: 6 })}>
        <Formatted.Number
          value={unrealizedPnl}
          withColor
          withPlus
          precision={getQuoteCurrencyPrecision(quoteCurrency)}
          toFixed
          css={{ textStyle: 'Title/M' }}
        />
        {calcUnrealizedPnlPercent != null && (
          <Formatted.Percent
            value={calcUnrealizedPnlPercent}
            withColor
            withPlus
            withParentheses
            precision={getQuoteCurrencyPrecision(quoteCurrency)}
            toFixed
            css={{ textStyle: 'Title/M' }}
          />
        )}
      </div>
      <dl
        className={flex({
          gap: 8,
          pt: 12,
          '& > div': {
            flex: 1,
            '& dt': {
              textStyle: 'Text/M/Regular',
              color: 'content.default.level-1',
            },
            '& dd': {
              pt: 4,
              color: 'inverse.white',
            },
          },
        })}
      >
        <div>
          <dt>
            <FormattedMessage id='Assets.r250401.Entry_price' />
          </dt>
          <dd>
            <Formatted.Number value={entryPrice} precision={pricePrecision} toFixed css={numberStyles} />
          </dd>
        </div>
        <div>
          <dt>
            <FormattedMessage id='Assets.r250401.Margin' />
          </dt>
          <dd>
            <Formatted.Number
              value={margin}
              precision={getQuoteCurrencyPrecision(quoteCurrency)}
              toFixed
              css={numberStyles}
            />
          </dd>
        </div>
      </dl>
    </section>
  );
};

export default FlipSideCard;

const numberStyles = css.raw({
  textStyle: 'Title/S',
  color: 'content.default.level-5',
});
